import React, { useState, useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    useRecordContext,
    TopToolbar,
    FilterButton,
    ExportButton,
    TextInput,
    SelectInput,
    DateInput,
    NumberInput,
    useDataProvider,
    downloadCSV
} from 'react-admin';
import { Chip, Box, Typography, Button, CircularProgress } from '@mui/material';
import jsonExport from 'jsonexport/dist';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';

// Colori per ogni status
const statusColors = {
    pending: '#ff9800',
    succeeded: '#4caf50',
    failed: '#f44336',
    canceled: '#9e9e9e',
    refunded: '#2196f3'
};

const statusIcons = {
    pending: <HourglassEmptyIcon />,
    succeeded: <CheckCircleIcon />,
    failed: <ErrorIcon />,
    canceled: <CancelIcon />,
    refunded: <MoneyOffIcon />
};

const statusLabels = {
    pending: 'In Attesa',
    succeeded: 'Completato',
    failed: 'Fallito',
    canceled: 'Annullato',
    refunded: 'Rimborsato'
};

// Componente per visualizzare lo status con colore e icona
const StatusField = () => {
    const record = useRecordContext();
    if (!record) return null;

    const status = record.status || 'pending';
    const color = statusColors[status];
    const icon = statusIcons[status];
    const label = statusLabels[status];

    return (
        <Chip
            icon={icon}
            label={label}
            sx={{
                backgroundColor: color,
                color: 'white',
                fontWeight: 'bold',
                '& .MuiChip-icon': {
                    color: 'white'
                }
            }}
        />
    );
};

// Componente per visualizzare l'importo formattato
const AmountField = () => {
    const record = useRecordContext();
    if (!record) return null;

    const amount = (record.amount / 100).toFixed(2);
    const currency = (record.currency || 'EUR').toUpperCase();

    return (
        <Typography variant="body2" fontWeight="bold">
            {amount} {currency}
        </Typography>
    );
};

// Componente per visualizzare l'utente con caricamento dati
const UserField = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!record || !record.usersId) {
            setUser(null);
            return;
        }

        setLoading(true);
        dataProvider.getOne('users', { id: record.usersId })
            .then(({ data }) => {
                setUser(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Errore caricamento utente:', error);
                setLoading(false);
            });
    }, [record, dataProvider]);

    if (!record) return null;

    if (!record.usersId) {
        return <Typography variant="body2" color="textSecondary">Guest</Typography>;
    }

    if (loading) {
        return <CircularProgress size={16} />;
    }

    if (user) {
        return (
            <Box>
                <Typography variant="body2" fontWeight="bold">
                    {user.nome || user.email}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    {user.email}
                </Typography>
            </Box>
        );
    }

    return (
        <Typography variant="body2" color="textSecondary">
            User ID: {record.usersId}
        </Typography>
    );
};

// Filtri avanzati
const transactionFilters = [
    <TextInput
        label="🔍 Payment Intent ID"
        source="stripe_payment_intent_id"
        alwaysOn
        resettable
    />,
    <SelectInput
        label="📊 Status"
        source="status"
        choices={[
            { id: 'pending', name: '🟠 In Attesa' },
            { id: 'succeeded', name: '🟢 Completato' },
            { id: 'failed', name: '🔴 Fallito' },
            { id: 'canceled', name: '⚪ Annullato' },
            { id: 'refunded', name: '🔵 Rimborsato' }
        ]}
        resettable
    />,
    <TextInput
        label="📝 Descrizione"
        source="description"
        resettable
    />,
    <NumberInput
        label="👤 User ID"
        source="usersId"
        resettable
    />,
    <NumberInput
        label="💰 Importo Min (€)"
        source="amount_gte"
        helperText="Importo minimo in euro"
        resettable
    />,
    <NumberInput
        label="💰 Importo Max (€)"
        source="amount_lte"
        helperText="Importo massimo in euro"
        resettable
    />,
    <DateInput
        label="📅 Data Da"
        source="created_at_gte"
        resettable
    />,
    <DateInput
        label="📅 Data A"
        source="created_at_lte"
        resettable
    />,
    <SelectInput
        label="💱 Valuta"
        source="currency"
        choices={[
            { id: 'eur', name: 'EUR - Euro' },
            { id: 'usd', name: 'USD - Dollaro' },
            { id: 'gbp', name: 'GBP - Sterlina' }
        ]}
        resettable
    />
];

// Funzione per export CSV personalizzato
const exporter = async (records, fetchRelatedRecords) => {
    // Prepara i dati per l'export
    const dataToExport = records.map(record => ({
        ID: record.id,
        'Payment Intent ID': record.stripe_payment_intent_id,
        Status: record.status,
        'Importo (€)': (record.amount / 100).toFixed(2),
        Valuta: (record.currency || 'EUR').toUpperCase(),
        Descrizione: record.description || '',
        'User ID': record.usersId || 'Guest',
        'Data Creazione': new Date(record.created_at).toLocaleString('it-IT'),
        'Ultimo Aggiornamento': new Date(record.updated_at).toLocaleString('it-IT')
    }));

    // Converti in CSV
    jsonExport(dataToExport, {
        headers: ['ID', 'Payment Intent ID', 'Status', 'Importo (€)', 'Valuta', 'Descrizione', 'User ID', 'Data Creazione', 'Ultimo Aggiornamento']
    }, (err, csv) => {
        if (err) {
            console.error('Errore export CSV:', err);
            return;
        }
        // Download del file
        const timestamp = new Date().toISOString().split('T')[0];
        downloadCSV(csv, `transazioni-stripe-${timestamp}`);
    });
};

// Actions personalizzate con export
const ListActions = () => (
    <TopToolbar>
        <FilterButton />
        <ExportButton />
    </TopToolbar>
);

export const StripeTransactionsList = (props) => {
    return (
        <List
            {...props}
            filters={transactionFilters}
            actions={<ListActions />}
            exporter={exporter}
            sort={{ field: 'created_at', order: 'DESC' }}
            perPage={25}
            title="💳 Transazioni Stripe"
        >
            <Datagrid
                bulkActionButtons={false}
                rowClick="show"
                sx={{
                    '& .RaDatagrid-rowCell': {
                        padding: '12px'
                    }
                }}
            >
                <TextField source="id" label="ID" />
                <StatusField label="Status" />
                <AmountField label="Importo" />
                <TextField source="description" label="Descrizione" />
                <UserField label="Utente" />
                <FunctionField
                    label="Payment Intent"
                    render={record => (
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {record.stripe_payment_intent_id}
                        </Typography>
                    )}
                />
                <DateField source="created_at" label="Data Creazione" showTime />
                <DateField source="updated_at" label="Ultimo Aggiornamento" showTime />
            </Datagrid>
        </List>
    );
};
